import { ApolloError } from "@apollo/client";
import {
  EnumEntityReferenceType,
  OrganizationByIdQuery,
  useOrganizationByIdQuery,
  UserFindOneByEmailQuery,
} from "gql/generated";
import {
  createContext,
  PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import { ChangeEvent } from "react";
import { useNavigate } from "react-router-dom";
import { useDocumentTitle } from "shared/hooks";

type OnboardingContextProviderInput = {
  organizationId: string | null;
  invitedFrom: EnumEntityReferenceType;
  skip: boolean;
  operatorId: string | null;
  operatorName: string | null;
  inviterEmail: string | null;
  isInvited: boolean;
  emailAddress: string;
} & PropsWithChildren;

type OnboardingScreen =
  | "invite"
  | "welcome"
  | "create"
  | "get-in-touch"
  | "coming-soon";

type OnboardingContextValues = {
  organization: OrganizationByIdQuery["organizationById"] | null;
  organizationError: ApolloError | undefined;
  organizationLoading: boolean;
  onEmailInputChange: (e: ChangeEvent<HTMLInputElement>) => void;
  screen: OnboardingScreen;
  onScreenChange: (screen: OnboardingScreen) => void;
  user?: UserFindOneByEmailQuery["userFindOneByEmail"];
  onUserFind?: (user: UserFindOneByEmailQuery["userFindOneByEmail"]) => void;
} & OnboardingContextProviderInput;

const context = createContext<OnboardingContextValues | null>(null);

export const OnboardingContextProvider = ({
  organizationId,
  invitedFrom,
  skip,
  operatorId,
  operatorName,
  inviterEmail,
  isInvited,
  emailAddress,
  children,
}: OnboardingContextProviderInput) => {
  useDocumentTitle();

  /**
   * Check to see if the user is already logged in
   * - If so, we can navigate them to the /app URL
   */
  const navigate = useNavigate();
  useEffect(() => {
    const auth0 = localStorage.getItem(
      `@@auth0spajs@@::${import.meta.env.VITE_AUTH0_CLIENT_ID}::@@user@@`,
    );
    if (auth0) {
      const info = JSON.parse(auth0);
      const email = info?.decodedToken?.user?.email;
      if (email) {
        navigate("/app");
      }
    }
  }, [navigate]);

  const [onboardingEmail, setOnboardingEmail] =
    useState<OnboardingContextProviderInput["emailAddress"]>(emailAddress);

  const [screen, setScreen] = useState<OnboardingScreen>("invite");

  const [user, setUser] = useState<
    UserFindOneByEmailQuery["userFindOneByEmail"] | undefined
  >(undefined);

  const onEmailInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setOnboardingEmail(e.target.value);
  };

  const onScreenChange = (screen: OnboardingScreen) => {
    setScreen(screen);
  };

  const onUserFind = (user: UserFindOneByEmailQuery["userFindOneByEmail"]) => {
    if (user) {
      setUser(user);
    }
  };

  // If there is an organizationId, load the organization details
  const {
    data: organization,
    error,
    loading,
  } = useOrganizationByIdQuery({ variables: { id: organizationId } });

  const contextValue: OnboardingContextValues = useMemo(
    () => ({
      organizationId,
      invitedFrom,
      organization: organization?.organizationById,
      organizationError: error,
      organizationLoading: loading,
      skip,
      operatorId,
      operatorName,
      inviterEmail,
      isInvited,
      emailAddress: onboardingEmail,
      onEmailInputChange,
      screen,
      onScreenChange,
      user,
      onUserFind,
    }),
    [
      organizationId,
      invitedFrom,
      organization?.organizationById,
      error,
      loading,
      skip,
      operatorId,
      operatorName,
      inviterEmail,
      isInvited,
      onboardingEmail,
      screen,
      user,
    ],
  );

  return <context.Provider value={contextValue}>{children}</context.Provider>;
};

export function useOnboardingContext() {
  const onboardingContext = useContext(context);

  if (!onboardingContext) {
    throw new Error(
      "useOnboardingContext must be used within a OperatorContextProvider",
    );
  }

  return onboardingContext;
}
